(() => {
  'use strict';

  // V5 editor safeguard: Shopify can reopen the last template (including 404).
  // We intentionally do not auto-redirect in design mode; the immersive 404
  // gives a direct homepage link without breaking the theme editor.


  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (a, b, t) => a + (b - a) * t;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  onReady(() => {
    document.querySelectorAll('[data-kgm-loader]').forEach((node) => node.remove());
    initGlobalScroll();
    initMagneticButtons();
    document.querySelectorAll('[data-kgm-cinematic]').forEach(initCinematicRooms);
    document.querySelectorAll('[data-kgm-scroll-studio]').forEach(initScrollStudio);
    document.querySelectorAll('[data-kgm-room-reel]').forEach(initRoomReel);
  });

  function initGlobalScroll() {
    const header = document.querySelector('[data-site-header]');
    const progress = document.querySelector('[data-kgm-scroll-progress]');

    const update = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      if (header) header.classList.toggle('is-scrolled', y > 40);
      if (progress) {
        const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
        progress.style.transform = `scaleX(${clamp(y / max, 0, 1)})`;
      }
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  function initMagneticButtons() {
    if (prefersReducedMotion || !window.matchMedia('(pointer:fine)').matches) return;

    document.addEventListener('pointermove', (event) => {
      const target = event.target.closest('.kgm-magnetic');
      document.querySelectorAll('.kgm-magnetic.is-magnetic').forEach((button) => {
        if (button !== target) {
          button.classList.remove('is-magnetic');
          button.style.transform = '';
        }
      });
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      if (Math.abs(dx) > rect.width || Math.abs(dy) > rect.height) return;

      target.classList.add('is-magnetic');
      target.style.transform = `translate3d(${dx * 0.12}px, ${dy * 0.18}px, 0)`;
    });

    document.addEventListener('pointerout', (event) => {
      const target = event.target.closest('.kgm-magnetic');
      if (!target) return;
      target.classList.remove('is-magnetic');
      target.style.transform = '';
    });
  }

  function initCinematicRooms(section) {
    const slides = [...section.querySelectorAll('[data-cinematic-slide]')];
    const dots = [...section.querySelectorAll('[data-cinematic-dot]')];
    const tabs = [...section.querySelectorAll('[data-cinematic-tab]')];
    const currentLabel = section.querySelector('[data-cinematic-current]');
    const panels = [...section.querySelectorAll('[data-cinematic-panel]')];
    if (!slides.length) return;

    let activeIndex = 0;
    let zoom = 1;
    let panX = 0;
    let panY = 0;
    let wheelAccumulator = 0;
    let lastWheelAt = 0;
    let dragging = false;
    let dragStart = null;
    let startPan = null;
    const pointers = new Map();
    let pinchStartDistance = 0;
    let pinchStartZoom = 1;

    const activeMedia = () => slides[activeIndex]?.querySelector('[data-cinematic-media]');

    const closePanels = () => {
      panels.forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      });
    };

    const renderTransform = () => {
      const media = activeMedia();
      if (!media) return;
      media.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${zoom})`;
    };

    const resetView = () => {
      zoom = 1;
      panX = 0;
      panY = 0;
      renderTransform();
    };

    const showSlide = (nextIndex, direction = 1) => {
      const normalized = (nextIndex + slides.length) % slides.length;
      if (normalized === activeIndex) return;

      const previous = slides[activeIndex];
      const previousMedia = previous?.querySelector('[data-cinematic-media]');
      if (previous) {
        previous.classList.add('is-exiting');
        previous.classList.remove('is-active');
        window.setTimeout(() => previous.classList.remove('is-exiting'), 1050);
      }
      if (previousMedia) previousMedia.style.transform = '';

      activeIndex = normalized;
      const next = slides[activeIndex];
      next.style.setProperty('--direction', direction);
      next.classList.add('is-active');

      dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      tabs.forEach((tab, index) => tab.classList.toggle('is-active', index === activeIndex));
      if (currentLabel) currentLabel.textContent = String(activeIndex + 1).padStart(2, '0');
      closePanels();
      resetView();
    };

    const adjustZoom = (amount, originX = section.clientWidth / 2, originY = section.clientHeight / 2) => {
      const oldZoom = zoom;
      zoom = clamp(zoom + amount, 1, 3.2);
      if (zoom === 1) {
        panX = 0;
        panY = 0;
      } else if (oldZoom !== zoom) {
        const rect = section.getBoundingClientRect();
        const cx = originX - rect.left - rect.width / 2;
        const cy = originY - rect.top - rect.height / 2;
        const ratio = zoom / oldZoom;
        panX = cx - (cx - panX) * ratio;
        panY = cy - (cy - panY) * ratio;
      }
      renderTransform();
    };

    dots.forEach((dot) => dot.addEventListener('click', () => showSlide(Number(dot.dataset.index), Number(dot.dataset.index) > activeIndex ? 1 : -1)));
    tabs.forEach((tab) => tab.addEventListener('click', () => showSlide(Number(tab.dataset.index), Number(tab.dataset.index) > activeIndex ? 1 : -1)));
    section.querySelectorAll('[data-cinematic-next]').forEach((button) => button.addEventListener('click', () => showSlide(activeIndex + 1, 1)));

    section.querySelector('[data-cinematic-zoom-in]')?.addEventListener('click', () => adjustZoom(0.35));
    section.querySelector('[data-cinematic-zoom-out]')?.addEventListener('click', () => adjustZoom(-0.35));
    section.querySelector('[data-cinematic-reset]')?.addEventListener('click', resetView);
    section.querySelector('[data-cinematic-fullscreen]')?.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        section.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    });

    section.querySelectorAll('[data-cinematic-hotspot]').forEach((hotspot) => {
      hotspot.addEventListener('click', () => {
        const panel = document.getElementById(hotspot.dataset.panelId);
        const wasOpen = panel?.classList.contains('is-open');
        closePanels();
        if (panel && !wasOpen) {
          panel.classList.add('is-open');
          panel.setAttribute('aria-hidden', 'false');
          panel.querySelector('button, a, input')?.focus({ preventScroll: true });
        }
      });
    });

    section.querySelectorAll('[data-cinematic-panel-close]').forEach((button) => button.addEventListener('click', closePanels));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closePanels();
      if (!section.matches(':hover') && !section.contains(document.activeElement)) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') showSlide(activeIndex + 1, 1);
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') showSlide(activeIndex - 1, -1);
      if (event.key === '+' || event.key === '=') adjustZoom(.25);
      if (event.key === '-') adjustZoom(-.25);
    });

    section.addEventListener('wheel', (event) => {
      const now = performance.now();

      if (zoom > 1.01 || event.ctrlKey) {
        event.preventDefault();
        adjustZoom(-event.deltaY * 0.0016, event.clientX, event.clientY);
        return;
      }

      const rect = section.getBoundingClientRect();
      const occupiesViewport = rect.top <= 2 && rect.bottom >= window.innerHeight - 2;
      if (!occupiesViewport) return;

      const direction = Math.sign(event.deltaY);
      const outward = (direction < 0 && activeIndex === 0) || (direction > 0 && activeIndex === slides.length - 1);
      if (outward) {
        wheelAccumulator = 0;
        return;
      }

      event.preventDefault();
      if (now - lastWheelAt > 260) wheelAccumulator = 0;
      wheelAccumulator += event.deltaY;
      if (Math.abs(wheelAccumulator) > 55 && now - lastWheelAt > 620) {
        showSlide(activeIndex + Math.sign(wheelAccumulator), Math.sign(wheelAccumulator));
        lastWheelAt = now;
        wheelAccumulator = 0;
      }
    }, { passive: false });

    const pointerDistance = () => {
      const values = [...pointers.values()];
      if (values.length < 2) return 0;
      return Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
    };

    section.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button, a, input, select, textarea, form')) return;
      section.setPointerCapture?.(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        dragging = true;
        dragStart = { x: event.clientX, y: event.clientY };
        startPan = { x: panX, y: panY };
      } else if (pointers.size === 2) {
        pinchStartDistance = pointerDistance();
        pinchStartZoom = zoom;
      }
    });

    section.addEventListener('pointermove', (event) => {
      if (pointers.has(event.pointerId)) {
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }

      if (pointers.size === 2 && pinchStartDistance > 0) {
        event.preventDefault();
        const distance = pointerDistance();
        zoom = clamp(pinchStartZoom * (distance / pinchStartDistance), 1, 3.2);
        if (zoom === 1) { panX = 0; panY = 0; }
        renderTransform();
        return;
      }

      if (dragging && dragStart && startPan) {
        const dx = event.clientX - dragStart.x;
        const dy = event.clientY - dragStart.y;
        if (zoom > 1.01) {
          panX = startPan.x + dx;
          panY = startPan.y + dy;
          renderTransform();
        }
        return;
      }

      if (!prefersReducedMotion && window.matchMedia('(pointer:fine)').matches && zoom === 1) {
        const rect = section.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - .5;
        const py = (event.clientY - rect.top) / rect.height - .5;
        const media = activeMedia();
        if (media) media.style.transform = `translate3d(${px * -18}px, ${py * -12}px, 0) scale(1.018)`;
      }
    });

    const endPointer = (event) => {
      const start = dragStart;
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinchStartDistance = 0;
      if (!pointers.size && dragging) {
        dragging = false;
        if (zoom === 1 && start) {
          const dx = event.clientX - start.x;
          if (Math.abs(dx) > 70) showSlide(activeIndex + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        }
        dragStart = null;
        startPan = null;
      }
    };

    section.addEventListener('pointerup', endPointer);
    section.addEventListener('pointercancel', endPointer);
    section.addEventListener('pointerleave', () => {
      if (!dragging && zoom === 1) {
        const media = activeMedia();
        if (media) media.style.transform = '';
      }
    });
  }

  function initScrollStudio(section) {
    const sticky = section.querySelector('.kgm-studio__sticky');
    const stage = section.querySelector('[data-studio-world]');
    const progressBar = section.querySelector('[data-studio-progress]');
    const material = section.querySelector('[data-craft-material]');
    const finalImage = section.querySelector('[data-studio-final]');
    const pieces = Object.fromEntries(
      [...section.querySelectorAll('[data-studio-object]')].map((node) => [node.dataset.studioObject, node])
    );
    const storySteps = [...section.querySelectorAll('[data-studio-story]')];
    const timelineLabels = [...section.querySelectorAll('[data-craft-label]')];
    const callouts = [...section.querySelectorAll('[data-craft-callout]')];
    if (!sticky || !stage) return;

    let pointerX = 0;
    let pointerY = 0;

    const ease = t => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
    const phase = (p, a, b) => ease((p - a) / Math.max(.001, b - a));
    const place = (node, from, to, t, opacityFloor=.18) => {
      if (!node) return;
      const k = ease(t);
      const x = lerp(from.x, to.x, k);
      const y = lerp(from.y, to.y, k);
      const z = lerp(from.z, to.z, k);
      const r = lerp(from.r, to.r, k);
      const s = lerp(from.s, to.s, k);
      node.style.transform = `translate3d(${x}px,${y}px,${z}px) rotateZ(${r}deg) scale(${s})`;
      node.style.opacity = String(opacityFloor + (1-opacityFloor)*k);
    };

    const render = () => {
      const rect = section.getBoundingClientRect();
      const total = Math.max(section.offsetHeight - window.innerHeight, 1);
      const p = clamp(-rect.top / total, 0, 1);
      const cut = phase(p, .06, .30);
      const assemble = phase(p, .18, .68);
      const functionPhase = phase(p, .48, .80);
      const finish = phase(p, .70, .96);

      if (progressBar) progressBar.style.transform = `scaleX(${p})`;
      const storyIndex = Math.min(3, Math.floor(p * 4));
      storySteps.forEach((step,i)=>step.classList.toggle('is-active', i===storyIndex));
      timelineLabels.forEach((label,i)=>label.classList.toggle('is-active', i===storyIndex));
      callouts.forEach((callout,i)=>callout.classList.toggle('is-active', storyIndex===i+1 || (storyIndex===3 && i===2)));

      if (material) {
        material.style.opacity = String(1 - phase(p,.12,.42)*.88);
        material.style.transform = `rotateX(${lerp(9,0,cut)}deg) rotateZ(${lerp(-4,0,cut)}deg) scale(${lerp(1.03,.92,cut)}) translate3d(${pointerX*8}px,${pointerY*6}px,0)`;
      }

      place(pieces.base,   {x:-260,y:210,z:-60,r:-16,s:.72}, {x:0,y:0,z:0,r:0,s:1}, assemble);
      place(pieces.drawer, {x:280,y:190,z:90,r:18,s:.66},    {x:0,y:0,z:26,r:0,s:1}, assemble);
      place(pieces.lower,  {x:-300,y:-30,z:130,r:-21,s:.62}, {x:0,y:0,z:54,r:0,s:1}, assemble);
      place(pieces.upper,  {x:250,y:-210,z:170,r:20,s:.58},  {x:0,y:0,z:92,r:0,s:1}, assemble);
      place(pieces.rods,   {x:40,y:-330,z:230,r:4,s:.5},     {x:0,y:0,z:118,r:0,s:1}, functionPhase, .08);

      const tiltX = pointerY * -4;
      const tiltY = pointerX * 5;
      stage.style.transform = `translate(-50%,-50%) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${1 + finish*.035})`;

      const buildOpacity = 1 - finish*.78;
      ['base','drawer','lower','upper','rods'].forEach(key=>{ if(pieces[key]) pieces[key].style.filter=`opacity(${buildOpacity})`; });
      if (finalImage) {
        finalImage.style.opacity = String(finish);
        finalImage.style.transform = `translate(-50%,-50%) translate3d(${pointerX*8}px,${pointerY*6}px,0) rotateY(${pointerX*5}deg) scale(${.91 + finish*.08})`;
      }
    };

    section.addEventListener('pointermove', event => {
      const rect = sticky.getBoundingClientRect();
      pointerX = clamp((event.clientX - rect.left) / rect.width - .5, -.5, .5);
      pointerY = clamp((event.clientY - rect.top) / rect.height - .5, -.5, .5);
      render();
    });
    section.addEventListener('pointerleave', () => { pointerX=0; pointerY=0; render(); });
    render();
    window.addEventListener('scroll', render, { passive:true });
    window.addEventListener('resize', render, { passive:true });
  }

  function initRoomReel(section) {
    const track = section.querySelector('[data-room-reel-track]');
    const progressBar = section.querySelector('[data-room-reel-progress]');
    if (!track) return;

    const render = () => {
      const rect = section.getBoundingClientRect();
      const total = Math.max(section.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-rect.top / total, 0, 1);
      const maxX = Math.max(track.scrollWidth - window.innerWidth, 0);
      track.style.transform = `translate3d(${-maxX * progress}px,0,0)`;
      if (progressBar) progressBar.style.transform = `scaleX(${progress})`;
    };

    render();
    window.addEventListener('scroll', render, { passive: true });
    window.addEventListener('resize', render, { passive: true });
  }
})();
